import React, { CSSProperties, MouseEvent, ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { trackMixpanel, useAppType, useNavigate } from "utils";

import { Checkbox, Divider, Drawer, Icon, IconProps, IconSizes, Link, Switch } from "components";

type ContextMenuItemProps = {
  icon: ReactElement<IconProps>;
  dataTestId?: string;
  iconPlacement?: string;
  state?: "warning" | "disabled";
  color?: string;
  index?: number;
  label?: string | ReactNode;
  style?: CSSProperties;
  onClick?: (e: MouseEvent) => void;
  hasToggle?: boolean;
  isToggled?: boolean;
  onToggle?: (checked: boolean) => void;
  hasCheckbox?: boolean;
  isChecked?: boolean;
  onCheck?: (checked: boolean) => void;
  isFirstItem?: boolean;
};

export type ItemProps = {
  iconName: string;
  label: ReactNode | string;
  state?: "warning" | "disabled";
  color?: string;
  style?: CSSProperties;
  url?: string;
  onClick?: () => void;
  hasToggle?: boolean;
  isToggled?: boolean;
  onToggle?: (checked: boolean) => void;
  hasCheckbox?: boolean;
  isChecked?: boolean;
  onCheck?: (checked: boolean) => void;
  isFirstItem?: boolean;
  disabled?: boolean;
};

export type ContextMenuProps = {
  children?: ReactNode;
  items: ItemProps[];
  placement: "right" | "left" | "bottom-left" | "bottom-right";
  visible: boolean;
  onClose: () => void;
  className?: string;
  dataTestId?: string;
  iconPlacement?: string;
  state?: string;
  iconSize?: IconSizes;
};

const getPlacementStyle = (placement: ContextMenuProps["placement"], parentDiv: HTMLDivElement | null, menuDiv: HTMLDivElement | null) => {
  const defaults = {
    position: "fixed" as React.CSSProperties["position"],
    maxHeight: "calc(100vh - 80px)", // Account for header height
    overflowY: "auto" as const, // Enable scrolling for long menus
  };

  if (!parentDiv || !menuDiv) {
    return defaults;
  }

  const { top, left, bottom, right } = parentDiv.getBoundingClientRect();
  const menuWidth = menuDiv.offsetWidth; // Get the width of the menu
  const headerHeight = 80; // Assuming header height is 80px
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const menuHeight = 400; // Approximate max height of menu
  const bottomPadding = 16; // Padding from bottom of viewport

  if (placement === "right") {
    const wouldOverflowBottom = top + menuHeight > viewportHeight - bottomPadding;
    const adjustedTop = wouldOverflowBottom ? Math.max(headerHeight, viewportHeight - menuHeight - bottomPadding) : top;

    return {
      ...defaults,
      top: `${adjustedTop + 20}px`,
      left: `calc(${right}px + 1rem)`,
    };
  }
  if (placement === "left") {
    return {
      ...defaults,
      top: `${top}px`,
      left: `calc(${left}px - ${menuWidth + 16}px)`,
    };
  }
  if (placement === "bottom-left") {
    return {
      ...defaults,
      top: `calc(${bottom}px + 1rem)`,
      left: `${left}px`,
    };
  }
  if (placement === "bottom-right") {
    const rightOffset = viewportWidth - right;
    return {
      ...defaults,
      top: `calc(${bottom}px + 1rem)`,
      right: `${rightOffset}px`,
      left: "auto",
    };
  }
};

const ContextMenuItem = ({
  dataTestId,
  icon: CtaIcon,
  iconPlacement = "left",
  state,
  index,
  label,
  style,
  onClick,
  hasToggle,
  isToggled,
  onToggle,
  hasCheckbox,
  isChecked,
  onCheck,
  isFirstItem,
}: ContextMenuItemProps) => (
  <div
    className={`menu-item ${iconPlacement === "right" ? "icon-right" : ""} ${state ? `label ${state}` : ""} ${isFirstItem ? "first-item" : ""}`}
    data-testid={`${dataTestId}-item-${index}`}
    onClick={(e) => {
      trackMixpanel("ClickContextMenuItem", { label });

      if (hasToggle && state !== "disabled") {
        onToggle?.(!isToggled);
      }

      onClick?.(e);
    }}
    style={style}
  >
    {iconPlacement === "left" && CtaIcon}
    <span className={`${state ? `${state}` : "label"}`}>{label}</span>
    <div className="menu-item-controls">
      {hasToggle && (
        <Switch
          checked={isToggled}
          onChange={(checked: boolean, event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (state !== "disabled") {
              onToggle?.(checked);
            }
          }}
          disabled={state === "disabled"}
          data-testid={`${dataTestId}-toggle`}
        />
      )}
      {hasCheckbox && (
        <Checkbox
          checked={isChecked}
          onChange={(e) => {
            e.stopPropagation();
            if (state !== "disabled") {
              onCheck?.(e.target.checked);
            }
          }}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          disabled={state === "disabled"}
          data-testid={`${dataTestId}-checkbox`}
        />
      )}
    </div>
    {iconPlacement === "right" && CtaIcon}
  </div>
);

export const ContextMenu = ({
  className = "",
  children,
  items,
  placement = "right",
  iconPlacement = "left",
  iconSize = "m",
  visible,
  onClose,
  dataTestId,
}: ContextMenuProps) => {
  const appType = useAppType();
  const navigate = useNavigate();
  const parentRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [placementStyle, setPlacementStyle] = useState(getPlacementStyle(placement, parentRef.current, menuRef.current));

  useEffect(() => {
    const updatePlacement = () => {
      setPlacementStyle(getPlacementStyle(placement, parentRef.current, menuRef.current));
    };

    updatePlacement();

    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, { passive: true });

    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement);
    };
  }, [placement, visible]);

  const handleDrawerClose = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const ContextMenuDesktop = (
    <div className="context-menu-container" data-testid={`${dataTestId}-container`} ref={parentRef}>
      {children}
      {visible &&
        createPortal(
          <>
            <div className="context-menu-mask" data-testid="context-menu-mask" onClick={handleDrawerClose} />
            <div
              className={`${className} context-menu-content`}
              style={placementStyle}
              ref={menuRef}
              data-testid={`${dataTestId}-content`}
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((item, index) => {
                const blockClick = item.hasCheckbox || item.hasToggle || item.state === "disabled";
                return (
                  <div key={index} className="context-menu-item" data-testid={`${dataTestId}-content-${index}`}>
                    <ContextMenuItem
                      {...item}
                      dataTestId={dataTestId}
                      icon={<Icon iconName={item.iconName} color={item.color} size={iconSize} />}
                      iconPlacement={iconPlacement}
                      state={item.state}
                      index={index}
                      label={item.label}
                      onClick={
                        blockClick
                          ? (e) => e.stopPropagation()
                          : item.url
                          ? () => {
                              navigate(item.url as string);
                              trackMixpanel("ClickLink", {
                                linkText: item.label,
                                link: item.url,
                              });
                            }
                          : (e) => {
                              handleDrawerClose(e);
                              item.onClick?.();
                            }
                      }
                      isFirstItem={item.isFirstItem}
                    />
                    {index < items.length - 1 ? <Divider /> : null}
                  </div>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </div>
  );

  const ContextMenuMobile = (
    <>
      {children}
      <Drawer className="bottom-drawer" closable={false} placement="bottom" visible={visible} onClose={onClose} destroyOnHidden={true}>
        <div className="bottom-drawer-handle" onClick={onClose}>
          <div />
        </div>
        {items.map((item, index) => (
          <div key={index} className="context-menu-item">
            {item.url ? (
              <Link to={item.url} mixpanelText={item.label as string}>
                <ContextMenuItem
                  {...item}
                  dataTestId={dataTestId}
                  icon={<Icon iconName={item.iconName} color={item.color} size={iconSize} />}
                  iconPlacement={iconPlacement}
                  state={item.state}
                  index={index}
                  label={item.label}
                />
              </Link>
            ) : (
              <ContextMenuItem
                {...item}
                dataTestId={dataTestId}
                icon={<Icon iconName={item.iconName} color={item.color} size={iconSize} />}
                iconPlacement={iconPlacement}
                state={item.state}
                index={index}
                label={item.label}
                onClick={item.onClick}
              />
            )}
            {index < items.length - 1 ? <Divider /> : null}
          </div>
        ))}
      </Drawer>
    </>
  );

  return appType.desktop ? ContextMenuDesktop : ContextMenuMobile;
};
